// =============================================================================
// domain/patternLeaderboard.ts — Setup Quality's headline card.
// -----------------------------------------------------------------------------
// Per-pattern performance: sample, W/L, win rate, total, profit factor,
// expectancy, average R, best and worst. Ported from `renderPatternLeaderboard`.
//
// ── 🚨 THE UNIT SPLIT IS DELIBERATE, AND IT IS THE SAME ONE `computeKpis` MAKES ──
//
// Win/loss and profit factor classify on the **$ SIGN**, which is unit-invariant.
// Total, expectancy, best and worst use the **display-unit value**, and a trade
// with no value in that unit is SKIPPED rather than counted as zero.
//
// Deriving the sign per unit instead would change which trades count as losses
// under `points` — a losing trade whose points figure is unavailable would silently
// become a scratch, and the win rate would climb for a reason nobody could see.
//
// ── THE TWO BUCKETS THAT ARE NOT PATTERNS ──
//
// `(Untagged)` collects trades with no `patternId`, and it is REPORTED rather than
// dropped: a leaderboard that silently omits half the book flatters every pattern
// on it. `(deleted pattern)` covers an id the catalogue no longer resolves — the
// trades still happened, and hiding them would move the totals.
// =============================================================================

/** What a row needs from a trade. Deliberately narrow. */
export interface LeaderboardTrade {
  patternId?: string | null;
  netPnl?: number | null;
  rrRealized?: number | null;
}

export interface LeaderboardStats {
  sample: number;
  wins: number;
  losses: number;
  /** Percent, 0–100. Scratches are excluded from the denominator. */
  winRate: number;
  totalPnl: number;
  /**
   * Gross win ÷ gross loss.
   *
   * ⚠ `Infinity` when there are winners and NO losses — a real state, and
   * distinct from 0 (no winners). The UI must render it as "∞", never as a
   * number, and sorting has to special-case it.
   */
  profitFactor: number;
  expectancy: number;
  /**
   * ⚠ NULL when no trade in the row recorded an R, never 0.
   *
   * The old client returns 0 here, and a pattern whose trades were all imported
   * without a planned stop then reads `+0.00` — a measured-looking flat average
   * beside a real win rate and a real profit factor. Same family as PD-16 on the
   * Analytics hero.
   */
  avgR: number | null;
  /** ⚠ NULL on a row with no value in the display unit — not 0, which is a real
   *  best trade. */
  bestPnl: number | null;
  worstPnl: number | null;
  /** How many of the row's trades had a value in the display unit. */
  valued: number;
  /** How many recorded a realised R — the population behind `avgR`. */
  rSample: number;
}

export interface LeaderboardRow {
  id: string;
  name: string;
  stats: LeaderboardStats;
}

export const UNTAGGED_ID = '__untagged__';

/** How a trade's value is read in the active display unit. `null` = no value. */
export type UnitValue = (t: LeaderboardTrade) => number | null;

export function computeLeaderboardStats(
  trades: readonly LeaderboardTrade[],
  unitValue: UnitValue,
): LeaderboardStats {
  // ⚠ THE SIGN, not the unit value. See the header.
  const wins = trades.filter((t) => (t.netPnl ?? 0) > 0);
  const losses = trades.filter((t) => (t.netPnl ?? 0) < 0);
  const grossWin = wins.reduce((s, t) => s + (t.netPnl ?? 0), 0);
  const grossLoss = losses.reduce((s, t) => s + Math.abs(t.netPnl ?? 0), 0);

  // ⚠ Scratches are in `sample` but NOT in the win-rate denominator, matching the
  // old client. A day of scratches must not read as a 0% win rate.
  const decided = wins.length + losses.length;
  const winRate = decided ? (wins.length / decided) * 100 : 0;
  const profitFactor = grossLoss > 0
    ? grossWin / grossLoss
    : (grossWin > 0 ? Infinity : 0);

  let totalPnl = 0;
  let valued = 0;
  let bestUv = -Infinity;
  let worstUv = Infinity;
  for (const t of trades) {
    const uv = unitValue(t);
    // ⚠ SKIPPED, not zeroed. A stop-less R or an uncataloged tick size has no
    // value in this unit; counting it as 0 would drag expectancy toward zero and
    // make the best/worst wrong.
    if (uv == null) continue;
    totalPnl += uv;
    valued++;
    if (uv > bestUv) bestUv = uv;
    if (uv < worstUv) worstUv = uv;
  }

  const rrs = trades
    .map((t) => t.rrRealized)
    .filter((r): r is number => r != null && Number.isFinite(r));

  return {
    sample: trades.length,
    wins: wins.length,
    losses: losses.length,
    winRate,
    totalPnl,
    profitFactor,
    expectancy: valued ? totalPnl / valued : 0,
    avgR: rrs.length ? rrs.reduce((a, b) => a + b, 0) / rrs.length : null,
    bestPnl: valued ? bestUv : null,
    worstPnl: valued ? worstUv : null,
    valued,
    rSample: rrs.length,
  };
}

/**
 * Group trades by pattern and compute a row for each.
 *
 * @param namePattern id → display name. An id it cannot resolve becomes
 *                    "(deleted pattern)" rather than vanishing.
 */
export function patternLeaderboard(
  trades: readonly LeaderboardTrade[],
  namePattern: (id: string) => string | null,
  unitValue: UnitValue,
): LeaderboardRow[] {
  const byPattern = new Map<string, LeaderboardTrade[]>();
  const untagged: LeaderboardTrade[] = [];

  for (const t of trades) {
    const pid = t.patternId ? String(t.patternId) : '';
    if (pid) {
      const bucket = byPattern.get(pid);
      if (bucket) bucket.push(t);
      else byPattern.set(pid, [t]);
    } else {
      untagged.push(t);
    }
  }

  const rows: LeaderboardRow[] = [];
  for (const [id, group] of byPattern) {
    rows.push({
      id,
      name: namePattern(id) || '(deleted pattern)',
      stats: computeLeaderboardStats(group, unitValue),
    });
  }
  // ⚠ Only when it has trades — an empty "(Untagged)" row is noise, and the old
  // client omits it for the same reason.
  if (untagged.length) {
    rows.push({
      id: UNTAGGED_ID,
      name: '(Untagged)',
      stats: computeLeaderboardStats(untagged, unitValue),
    });
  }
  return rows;
}

export type LeaderboardSortKey =
  | 'name' | 'sample' | 'winRate' | 'totalPnl'
  | 'profitFactor' | 'expectancy' | 'avgR' | 'bestPnl' | 'worstPnl';

/**
 * Sort a leaderboard.
 *
 * `Infinity` is clamped for COMPARISON only, so `Infinity - Infinity` cannot
 * produce `NaN` when two loss-free patterns are compared.
 *
 * ⚠ DEFENSIVE, and honestly labelled as such: no test in this suite can make its
 * removal fail. V8 treats a `NaN` comparator result as `0`, so at realistic row
 * counts the clamped and unclamped versions produce the same order — a negative
 * control that deletes `fin` stays green. It is kept because an inconsistent
 * comparator is unspecified behaviour rather than a guarantee, not because
 * anything here demonstrates a break. Do not add a 🎯 test claiming otherwise
 * without first showing it fail.
 */
export function sortLeaderboard(
  rows: readonly LeaderboardRow[],
  key: LeaderboardSortKey,
  dir: 'asc' | 'desc',
): LeaderboardRow[] {
  const fin = (v: number) => (v === Infinity ? 9999 : v === -Infinity ? -9999 : v);
  /**
   * ⚠ AN UNMEASURED ROW SORTS TO THE BOTTOM of an ascending sort rather than
   * being read as zero. A pattern whose trades recorded no R is not "average
   * R = 0"; ranking it among the flat ones would put it in the middle of the
   * table, which is the one place a reader stops looking.
   */
  const val = (r: LeaderboardRow): number => {
    const v = r.stats[key as Exclude<LeaderboardSortKey, 'name'>];
    return v == null ? -Infinity : fin(v);
  };
  const cmp = (a: LeaderboardRow, b: LeaderboardRow): number => {
    if (key === 'name') return a.name.localeCompare(b.name);
    return val(a) - val(b);
  };
  const out = [...rows].sort(cmp);
  return dir === 'desc' ? out.reverse() : out;
}
